/** Customer-facing gallery backed by the current public publication list. */
(() => {
  if (customElements.get('lora-work-gallery')) return;
  const MEDIA = 'https://lora-work-gallery.hipuikirott.chatgpt.site/api/media/';
  const normalise = (text) => String(text || '').normalize('NFKC').toLowerCase()
    .replace(/[״"׳']/g, '').replace(/חדרי שינה/g, 'חדר שינה')
    .replace(/מטבחים/g, 'מטבח').replace(/קירות/g, 'קיר')
    .replace(/אפורים|אפורה/g, 'אפור').replace(/סלונים/g, 'סלון');

  class LoraWorkGallery extends HTMLElement {
    #controller;
    #assets = [];
    #matches = [];
    #category = '';
    #style = '';
    #limit = 24;
    #selected;
    #returnFocus;
    #oldOverflow = '';
    #timer;

    connectedCallback() {
      if (this.#controller) return;
      this.#controller = new AbortController();
      this.labels = JSON.parse(this.querySelector('[data-gallery-labels]').textContent);
      this.grid = this.querySelector('[data-gallery-grid]');
      this.search = this.querySelector('[data-gallery-search]');
      this.dialog = this.querySelector('dialog');
      this.more = this.querySelector('[data-gallery-more]');
      const options = { signal: this.#controller.signal };
      this.addEventListener('click', (event) => this.#click(event), options);
      this.search.addEventListener('input', () => {
        clearTimeout(this.#timer);
        this.#timer = setTimeout(() => this.#filter(), 150);
      }, options);
      this.dialog.addEventListener('close', () => {
        document.documentElement.style.overflow = this.#oldOverflow;
        this.#returnFocus?.focus({ preventScroll: true });
      }, options);
      const fullImage = this.querySelector('[data-gallery-full]');
      fullImage.addEventListener('load', () => {
        fullImage.hidden = false;
        this.querySelector('[data-gallery-image-status]').hidden = true;
        this.querySelector('.work-gallery__lightbox').setAttribute('aria-busy', 'false');
      }, options);
      fullImage.addEventListener('error', () => {
        fullImage.hidden = true;
        const status = this.querySelector('[data-gallery-image-status]');
        status.textContent = this.labels.unavailable;
        status.hidden = false;
        this.querySelector('.work-gallery__lightbox').setAttribute('aria-busy', 'false');
      }, options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.target.closest('a, input, select, textarea')) return;
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.#step(event.key === (rtl ? 'ArrowLeft' : 'ArrowRight') ? 1 : -1);
      }, options);
      void this.#load();
    }

    disconnectedCallback() {
      if (this.dialog?.open) this.dialog.close();
      clearTimeout(this.#timer);
      this.#controller?.abort();
      this.#controller = null;
    }

    async #load() {
      const timeout = setTimeout(() => this.#controller?.abort(), 15000);
      try {
        const order = new Map([...this.grid.querySelectorAll('[data-gallery-open]')]
          .map((link, index) => [link.dataset.galleryOpen, index]));
        const response = await fetch(this.dataset.catalogue, {
          signal: this.#controller.signal, credentials: 'omit', cache: 'no-store'
        });
        if (!response.ok) throw new Error('Catalogue unavailable');
        const data = await response.json();
        if (!Array.isArray(data.assets)) throw new Error('Invalid catalogue');
        this.#assets = data.assets.filter((asset) => /^[a-f0-9]{64}$/.test(asset.id))
          .sort((a, b) => (order.get(a.id) ?? order.size) - (order.get(b.id) ?? order.size));
        // A featured image that is unpublished must disappear from the storefront too.
        const approved = new Set(this.#assets.map((asset) => asset.id));
        const featured = this.querySelector('.work-gallery__featured');
        for (const link of featured.querySelectorAll('[data-gallery-open]')) {
          link.hidden = !approved.has(link.dataset.galleryOpen);
        }
        featured.dataset.count = featured.querySelectorAll('[data-gallery-open]:not([hidden])').length;
        featured.hidden = featured.dataset.count === '0';
        this.#matches = this.#assets;
        this.setAttribute('data-ready', '');
        for (const control of this.querySelectorAll('[data-gallery-controls]')) control.hidden = false;
        this.#render();
        // A shared photograph can be opened directly without selecting a category first.
        const selected = new URL(location.href).searchParams.get('photo');
        if (selected && this.#assets.some((asset) => asset.id === selected)) this.#open(selected);
      } catch (error) {
        if (!this.isConnected) return;
        this.querySelector('[data-gallery-error]').hidden = false;
      } finally {
        clearTimeout(timeout);
      }
    }

    #click(event) {
      const target = event.target.closest('a, button');
      if (!target || !this.contains(target)) return;
      if (target.hasAttribute('data-gallery-open') && this.#assets.length) {
        event.preventDefault();
        this.#open(target.dataset.galleryOpen, target);
      }
      if (target.hasAttribute('data-gallery-category')) {
        this.#category = target.dataset.galleryCategory;
        for (const button of this.querySelectorAll('[data-gallery-category]')) {
          button.setAttribute('aria-pressed', String(button === target));
        }
        this.#filter();
      }
      if (target.hasAttribute('data-gallery-style')) {
        this.#style = target.dataset.galleryStyle === this.#style ? '' : target.dataset.galleryStyle;
        for (const button of this.querySelectorAll('[data-gallery-style]')) {
          button.setAttribute('aria-pressed', String(button.dataset.galleryStyle === this.#style));
        }
        this.#filter();
      }
      if (target.hasAttribute('data-gallery-reset')) {
        this.search.value = '';
        this.#category = '';
        this.#style = '';
        for (const button of this.querySelectorAll('[data-gallery-category]')) {
          button.setAttribute('aria-pressed', String(button.dataset.galleryCategory === ''));
        }
        for (const button of this.querySelectorAll('[data-gallery-style]')) button.setAttribute('aria-pressed', 'false');
        this.#filter();
        this.search.focus();
      }
      if (target.hasAttribute('data-gallery-more')) {
        const previous = this.#limit;
        this.#limit += 24;
        this.#render(true);
        this.grid.querySelectorAll('[data-gallery-open]')[previous]?.focus({ preventScroll: true });
      }
      if (target.hasAttribute('data-gallery-close')) this.dialog.close();
      if (target.hasAttribute('data-gallery-prev')) this.#step(-1);
      if (target.hasAttribute('data-gallery-next')) this.#step(1);
    }

    #filter() {
      this.#limit = 24;
      const terms = normalise(`${this.search.value} ${this.#style}`).split(/\s+/).filter(Boolean);
      this.#matches = this.#assets.filter((asset) => {
        if (this.#category && asset.space !== this.#category) return false;
        const content = normalise([asset.description, asset.categoryLabel, ...(asset.tags || []),
          ...(asset.models || []).map((model) => model.code)].join(' '));
        return terms.every((term) => content.includes(term));
      });
      this.#render();
    }

    #render(append = false) {
      const fragment = document.createDocumentFragment();
      const visible = this.#matches.slice(0, this.#limit);
      const start = append ? this.grid.children.length : 0;
      for (const asset of visible.slice(start)) fragment.append(this.#card(asset));
      if (append) this.grid.append(fragment);
      else this.grid.replaceChildren(fragment);
      const text = this.labels.results.replace('%shown%', visible.length).replace('%total%', this.#matches.length);
      this.querySelector('[data-gallery-count]').textContent = text;
      this.querySelector('[data-gallery-progress]').textContent = text;
      this.more.hidden = this.#matches.length <= this.#limit;
      this.querySelector('[data-gallery-empty]').hidden = this.#matches.length > 0;
      this.querySelector('[data-gallery-reset]').hidden = !this.#category && !this.#style && !this.search.value;
    }

    #card(asset) {
      const article = document.createElement('article');
      article.className = 'work-gallery__card';
      const link = document.createElement('a');
      link.className = 'work-gallery__card-link';
      link.href = MEDIA + asset.id;
      link.dataset.galleryOpen = asset.id;
      link.setAttribute('aria-label', `${this.labels.open}: ${asset.description}`);
      const media = document.createElement('span');
      media.className = 'work-gallery__card-media';
      const image = document.createElement('img');
      image.className = 'work-gallery__image';
      image.src = MEDIA + asset.id + '?thumb';
      image.alt = asset.description;
      image.width = 640;
      image.height = 800;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.referrerPolicy = 'no-referrer';
      image.addEventListener('error', () => {
        // Original fallback still checks publication permission on the gallery server.
        if (image.src.includes('?thumb')) image.src = MEDIA + asset.id;
        else { media.classList.add('work-gallery__card-media--unavailable'); image.hidden = true; }
      });
      const unavailable = document.createElement('span');
      unavailable.className = 'work-gallery__unavailable';
      unavailable.textContent = this.labels.unavailable;
      const expand = document.createElement('span');
      expand.className = 'work-gallery__expand';
      expand.textContent = '+';
      expand.setAttribute('aria-hidden', 'true');
      media.append(image, unavailable, expand);
      const caption = document.createElement('span');
      caption.className = 'work-gallery__caption';
      const category = document.createElement('span');
      category.className = 'work-gallery__category';
      category.textContent = this.labels.spaces[asset.space] || asset.categoryLabel;
      const title = document.createElement('span');
      title.className = 'work-gallery__card-title';
      title.textContent = asset.description;
      caption.append(category, title);
      link.append(media, caption);
      article.append(link);
      return article;
    }

    #open(id, trigger) {
      const asset = this.#assets.find((item) => item.id === id);
      if (!asset) return;
      if (!this.dialog.open) {
        this.#returnFocus = trigger || this.search;
        this.#oldOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
      }
      this.#selected = id;
      const image = this.querySelector('[data-gallery-full]');
      image.hidden = true;
      const status = this.querySelector('[data-gallery-image-status]');
      status.textContent = this.labels.loading;
      status.hidden = false;
      this.querySelector('.work-gallery__lightbox').setAttribute('aria-busy', 'true');
      image.src = MEDIA + id;
      image.alt = asset.description;
      this.querySelector('[data-gallery-title]').textContent = asset.description;
      this.querySelector('[data-gallery-space]').textContent = this.labels.spaces[asset.space] || asset.categoryLabel;
      const shared = new URL(location.href);
      shared.search = '';
      shared.searchParams.set('photo', id);
      shared.hash = '';
      const message = `${this.labels.message}\n${asset.description}\n${shared.href}\n${MEDIA}${id}`;
      const whatsapp = new URL('https://wa.me/972509445045');
      whatsapp.searchParams.set('text', message);
      this.querySelector('[data-gallery-whatsapp]').href = whatsapp.href;
      this.querySelector('[data-gallery-original]').href = MEDIA + id;
      const modelLinks = this.querySelector('[data-gallery-models]');
      modelLinks.replaceChildren();
      for (const model of asset.models || []) {
        try {
          const url = new URL(model.url);
          if (url.protocol !== 'https:' || !['hipuikir.com', 'www.hipuikir.com'].includes(url.hostname)) continue;
          const link = document.createElement('a');
          link.className = 'work-gallery__model';
          link.href = url.href;
          link.textContent = `${this.labels.model} ${model.code}`;
          modelLinks.append(link);
        } catch { /* A missing or invalid model URL is not shown. */ }
      }
      const index = this.#matches.findIndex((item) => item.id === id);
      this.querySelector('[data-gallery-prev]').disabled = index <= 0;
      this.querySelector('[data-gallery-next]').disabled = index < 0 || index >= this.#matches.length - 1;
      this.querySelector('[data-gallery-position]').textContent = index < 0 ? '' : `${index + 1} / ${this.#matches.length}`;
      if (!this.dialog.open) this.dialog.showModal();
    }

    #step(direction) {
      const index = this.#matches.findIndex((item) => item.id === this.#selected);
      const next = this.#matches[index + direction];
      if (next) this.#open(next.id);
    }
  }
  customElements.define('lora-work-gallery', LoraWorkGallery);
})();
